<template>
  <div class="ibox">
    <div class="ibox-title">
      Relatório DFe
      <small class="d-block text-muted mt-1">
        Volumetria de documentos fiscais por conta, mês e tipo de nota. O total de NFSe já inclui as notas do NFSe Nacional.
      </small>
    </div>

    <div class="ibox-content">
      <h5 class="text-muted mb-3">Filtros</h5>

      <div class="row">
        <div class="form-group col-md-12">
          <label for="accounts">
            Contas
            <small class="text-muted">(vazio = todas as contas)</small>
          </label>
          <Select2 id="accounts" :options="accounts" v-model="query.accountIds" title-load="Carregando contas..."/>
        </div>

        <div class="form-group col-md-4">
          <label for="start-date">Data inicial *</label>
          <input v-model="query.startDate" type="date" id="start-date" class="form-control input">
        </div>

        <div class="form-group col-md-4">
          <label for="end-date">Data final *</label>
          <input v-model="query.endDate" type="date" id="end-date" class="form-control input">
        </div>

        <div class="form-group col-md-4">
          <label for="period-type">Tipo de período</label>
          <select v-model="query.periodType" id="period-type" class="form-control input">
            <option value="created_at">Data de inclusão</option>
            <option value="issued_at">Data de emissão</option>
          </select>
        </div>
      </div>

      <hr>

      <h5 class="text-muted mb-3">Destinatários</h5>

      <div class="row">
        <div class="form-group col-md-8">
          <label for="email">E-mails *</label>
          <div class="input-group">
            <input v-model="inputString"
                   id="email"
                   class="form-control input"
                   placeholder="nome@empresa.com.br"
                   :style="emailError ? { borderColor: 'red' } : {}"
                   @keyup.enter="add"
            >
            <div class="input-group-append">
              <button class="btn btn-outline" :disabled="!validEmail" @click="add">Adicionar</button>
              <button class="btn btn-outline" :disabled="!myEmail || added(myEmail)" @click="addMyEmail">
                Usar meu e-mail
              </button>
            </div>
          </div>
          <small v-if="emailError" class="text-danger">{{ emailError }}</small>
          <small v-else class="text-muted">Pressione Enter para adicionar</small>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <div v-if="emails.length === 0" class="text-muted">
            Nenhum e-mail adicionado. O relatório é enviado por e-mail quando fica pronto.
          </div>

          <div v-for="email in emails" class="badge badge-info mr-1 mb-1 px-2 py-1" :key="email">
            <div class="badge-content">
              <span class="mr-1">{{ email }}</span>
              <span title="Remover" @click="removeEmail(email)">
                <XIcon size="1.5x" style="cursor: pointer"/>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="ibox-footer d-flex justify-content-end align-items-center">
      <small v-if="pending" class="text-muted mr-3">{{ pending }}</small>
      <button class="btn btn-primary" @click="requestReport" :disabled="!!pending">
        Agendar relatório
      </button>
    </div>
  </div>
</template>

<script>
  import GetUser from '@/mixins/GetUser'
  import GlobalLoading from '@/mixins/GlobalLoading'
  import Select2 from '@/components/shares/Select2'
  import { XIcon } from 'vue-feather-icons'

  const regex = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  const validateEmail = (email) => String(email).toLowerCase().match(regex)

  export default {
    components: { Select2, XIcon },
    computed: {
      emailError() {
        if (!this.inputString) return null
        if (!validateEmail(this.inputString)) return 'E-mail inválido'
        if (this.added(this.inputString)) return 'E-mail já adicionado'

        return null
      },
      myEmail() {
        return this.getUser && this.getUser.email
      },
      pending() {
        if (!this.query.startDate || !this.query.endDate) return 'Informe a data inicial e a data final'
        if (this.query.startDate > this.query.endDate) return 'A data inicial deve ser anterior à data final'
        if (this.emails.length === 0) return 'Adicione ao menos um e-mail'

        return null
      },
      validEmail() {
        return !!validateEmail(this.inputString) && !this.added(this.inputString)
      }
    },
    created() {
      this.buildFilter()
    },
    data: () => ({
      accounts: null,
      emails: [],
      inputString: '',
      query: {
        periodType: 'issued_at'
      }
    }),
    methods: {
      add() {
        if (!this.validEmail) return

        this.emails.push(this.inputString)
        this.inputString = ''
      },
      added(email) {
        return this.emails.includes(email)
      },
      addMyEmail() {
        if (this.myEmail && !this.added(this.myEmail))
          this.emails.push(this.myEmail)
      },
      buildFilter() {
        this.axios.get('/admin/events/filter_options')
          .then(response => this.accounts = response.data.accounts.map(a => ({ label: `${a.id} - ${a.name}`, value: a.id })))
      },
      removeEmail(email) {
        const index = this.emails.findIndex(e => e === email)
        this.emails.splice(index, 1)
      },
      requestReport() {
        const data = this.$parse.toSnakecase(this.query)
        data.emails = this.emails

        this.openGlobalLoad()
        this.axios.post('/admin/reports/dfe', data)
          .then(response => this.$notify.add(this.noticeOf(response)))
          .catch(error => this.$notify.addError(error))
          .finally(() => this.closeGlobalLoad())
      },
      noticeOf(response) {
        return (response && response.meta && response.meta.notice) ||
          'Relatório agendado. Será enviado para o(s) e-mail(s) selecionado(s)'
      }
    },
    mixins: [GetUser, GlobalLoading],
    name: 'DFe',
  }
</script>

<style scoped>
  .ibox-title small {
    font-weight: normal;
  }
</style>
